import React, { useState, useEffect, useContext, useMemo } from "react";
import { useQuery, gql, useMutation } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import AddShiftSidebar from "./AddShiftSidebar";
import ShiftsCalendarView from "./ShiftsCalendarView";
import ShiftsListingView from "./ShiftsListingView";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import "../../starek.css";

const DAYS_OF_WEEK = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

const formatTime = (sec) => {
    if (sec == null) return "-";
    const d = new Date(0);
    d.setSeconds(sec);
    let h = d.getUTCHours();
    const m = d.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
};

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Shifts",
    SEARCH_PLACEHOLDER: "Search",
    VIEW_MODE_TIMELINE: "Timeline View",
    VIEW_MODE_LIST: "List View",
};

const runsOnToDays = (mask) => {
    const days = [];
    DAYS_OF_WEEK.forEach((day, i) => {
        if (mask & (1 << i)) days.push(day.slice(0, 3));
    });
    return days.length ? days.join(", ") : "-";
};

const parseShiftData = (shifts) => {
    const dayShifts = DAYS_OF_WEEK.map((day) => ({ day, ranges: [] }));
    shifts.forEach((shift) => {
        const { start_time, end_time, runs_on } = shift;
        DAYS_OF_WEEK.forEach((_, index) => {
            if (runs_on & (1 << index)) {
                dayShifts[index].ranges.push({
                    shift_id: shift.id,
                    shift_name: shift.name,
                    start: start_time,
                    end: end_time,
                });
            }
        });
    });
    return dayShifts;
};

const GET_SHIFT_LIST_FOR_PLANT = gql`
    query GetShifts($plantId: Int!) {
        shifts(where: { plant_id: { equals: $plantId } }) {
            id
            name
            description
            start_time
            end_time
            runs_on
            created_at
            updated_at
            created_by {
                id
                name
            }
            updated_by {
                id
                name
            }
        }
    }
`;

const DELETE_MANY_SHIFTS = gql`
    mutation DeleteManyShift($ids: [Int!]!) {
        deleteManyShift(where: { id: { in: $ids } }) {
            count
        }
    }
`;

const DELETE_ONE_SHIFT = gql`
    mutation DeleteOneShift($id: Int!) {
        deleteOneShift(where: { id: $id }) {
            id
        }
    }
`;

// Confirmation messages
const SHIFT_MESSAGES = {
    DELETE_SHIFTS_BULK: (count) => `You are about to delete ${count} shift(s).

This action cannot be undone.

Are you sure you wish to continue?`,
};

const Shifts = () => {
    const starekContext = useContext(StarekContext);

    const { data, loading, error, refetch, client } = useQuery(
        GET_SHIFT_LIST_FOR_PLANT,
        {
            client: starekContext.engineClient,
            variables: { plantId: starekContext.lastUsedPlant.id },
        }
    );

    const [items, setItems] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [viewMode, setViewMode] = useState("timeline"); // "timeline" | "list"
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [showAddShiftDrawer, setShowAddShiftDrawer] = useState(false);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });

    const [deleteManyShifts] = useMutation(DELETE_MANY_SHIFTS, {
        client: starekContext.engineClient,
    });
    const [deleteOneShift] = useMutation(DELETE_ONE_SHIFT, {
        client: starekContext.engineClient,
    });

    useEffect(() => {
        if (data?.shifts) setItems(data.shifts);
    }, [data]);

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q)
        );
    }, [items, filterText]);

    const handleAdd = () => {
        setShowAddShiftDrawer(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        setShowAddShiftDrawer(true);
    };

    const handleDrawerClose = async () => {
        setShowAddShiftDrawer(false);
        await refetch();
        setToggleCleared((p) => !p);
    };

    const handleBulkDelete = async () => {
        if (selectedRows.length === 0) return;
        setIsDeleting(true);
        setDeleteDone(0);
        const ids = selectedRows.map((r) => r.id);

        try {
            await deleteManyShifts({ variables: { ids } });
            setDeleteDone(ids.length);
        } catch (_) {
            for (const id of ids) {
                try {
                    await deleteOneShift({ variables: { id } });
                    setDeleteDone((d) => d + 1);
                } catch (err) {
                    starekContext.showError(err);
                    break;
                }
            }
        } finally {
            setIsDeleting(false);
            setToggleCleared((p) => !p);
            setConfirmCfg({ open: false });
            await refetch();
        }
    };

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{ singular: "Shift", plural: "Shifts" }}
                allowSearch={true}
                allowAdd={true}
                allowDelete={true}
                allowEdit={true}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    setSelectedRows([]);
                }}
                onDelete={handleBulkDelete}
                onEdit={handleEdit}
            >
                <EntityOperationBar.View
                    mode="timeline"
                    icon="fa fa-calendar"
                    tooltip={UI_STRINGS.VIEW_MODE_TIMELINE}
                >
                    <ShiftsCalendarView
                        items={filteredItems}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        onEventClick={(shift) => {
                            setSelectedRows([shift]);
                            setShowAddShiftDrawer(true);
                        }}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip={UI_STRINGS.VIEW_MODE_LIST}
                >
                    <ShiftsListingView
                        items={filteredItems}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <AddShiftSidebar
                show={showAddShiftDrawer}
                closeOffcanvas={handleDrawerClose}
                starekContext={starekContext}
                item={selectedRows[0] || null}
            />

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                message={confirmCfg.message}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </div>
    );
};

export { Shifts };
